import { useCookie, useHead, useState } from 'nuxt/app'

import {
  BRAND_CSS_MAP,
  DARK_MODE_ON_COLOR_MIN_CONTRAST,
  DEFAULT_BRAND_TOKENS,
  DEFAULT_FONT_SCALE,
  HIGHRISE_BRIDGE_MAP,
  ON_COLOR_BINDINGS,
  THEME_COOKIE_KEY,
  THEME_COOKIE_MAX_AGE_SECONDS,
  TOKEN_CSS_MAP,
  TOKEN_SCALE_NAMES,
} from '../constants/theme.constants'
import { Theme, type BrandTokenModes, type BrandTokens } from '../types/theme'
import { sanitizeBrandTokens } from '../utils/brand-color'
import { pickOnColor, type OnColorStrategyOptions } from '../utils/color'
import { applyColorScale, getColorScale, removeColorScale } from '../utils/color-scale'
import { parseThemePreference } from '../utils/theme-cookie'

// Convenience re-exports so callers can import types/constants alongside useTheme.
export { COMMON_DARK_SIDEBAR_COLOR } from '../constants/theme.constants'
export { Theme }
export type { BrandTokenModes, BrandTokens }

// theme-layer/composables/useTheme.ts
export const useTheme = () => {
  const themeCookie = useCookie<string | null>(THEME_COOKIE_KEY, {
    default: () => null,
    sameSite: 'lax',
    path: '/',
    maxAge: THEME_COOKIE_MAX_AGE_SECONDS,
  })

  const currentTheme = useState<Theme>('cp-current-theme', getInitialTheme)
  const activeBrandTokens = useState<BrandTokens | null>('cp-active-brand-tokens', () => null)
  const includeBrandChromeInDark = useState<boolean>('cp-include-brand-chrome-in-dark', () => false)
  const activeBrandTokenModes = useState<BrandTokenModes | null>('cp-active-brand-token-modes', () => null)
  const activeThemeRootVariables = useState<Record<string, string>>('cp-active-theme-root-variables', () => ({}))
  const themeRootStyle = useState('cp-theme-root-style', () => '')

  useHead(() => ({
    htmlAttrs: {
      'data-theme': currentTheme.value,
    },
    style: themeRootStyle.value ? [{ key: 'cp-brand-theme', innerHTML: themeRootStyle.value }] : [],
  }))

  /** Guest / SSR preference from cookie (works on server and client). */
  function getStoredTheme(): Theme {
    return parseThemePreference(themeCookie.value) ?? Theme.LIGHT
  }

  /** Persist preference in cookie so the next SSR request paints correctly. */
  function storeTheme(theme: Theme) {
    themeCookie.value = theme
  }

  function getInitialTheme(): Theme {
    return getStoredTheme()
  }

  function applyTheme(theme: Theme) {
    currentTheme.value = theme
    if (process.server) return
    document.documentElement.setAttribute('data-theme', theme)
  }

  function reapplyBrandTokensAfterThemeChange() {
    if (process.server) return

    const root = document.documentElement

    // Remove inline brand token styles to allow CSS theme.css to take over
    // The CSS file defines different values for light/dark via [data-theme] selector
    clearBrandTokenOverrides(root)

    if (activeBrandTokenModes.value) {
      activeBrandTokens.value = activeBrandTokenModes.value[currentTheme.value]
      themeRootStyle.value = buildThemeModesRootCss(activeBrandTokenModes.value, activeThemeRootVariables.value)
      return
    }

    // Use requestAnimationFrame to ensure CSS has re-evaluated after attribute change
    requestAnimationFrame(() => {
      if (activeBrandTokens.value) {
        applyBrandTokens(activeBrandTokens.value, currentTheme.value, themeRootStyle, includeBrandChromeInDark.value)
      } else {
        themeRootStyle.value = ''
      }
    })
  }

  /**
   * Visual-only theme change for temporary shell overrides (e.g. light-only custom
   * community themes). Does not write the preference cookie or user profile.
   */
  function applySessionTheme(theme: Theme) {
    applyTheme(theme)
    reapplyBrandTokensAfterThemeChange()
  }

  function setTheme(theme: Theme) {
    // Apply attribute early to let CSS respond immediately
    applyTheme(theme)
    storeTheme(theme)

    if (process.server) return

    reapplyBrandTokensAfterThemeChange()
  }

  function setBrandTokens(tokens: Partial<BrandTokens>, options: { includeChromeInDark?: boolean } = {}) {
    const safeTokens = sanitizeBrandTokens(tokens)
    if (Object.keys(safeTokens).length === 0) return

    const resolvedTokens = resolveBrandTokens(safeTokens, activeBrandTokens.value)
    if (!resolvedTokens) return

    // Location / community writers own this path. Clear any events-only modes
    // so leaving SDUI pages restores the existing module theme.
    activeBrandTokenModes.value = null
    activeThemeRootVariables.value = {}
    activeBrandTokens.value = resolvedTokens
    includeBrandChromeInDark.value = options.includeChromeInDark ?? false
    applyBrandTokens(resolvedTokens, currentTheme.value, themeRootStyle, includeBrandChromeInDark.value)
  }

  /** Events SDUI only. Existing modules keep calling `setBrandTokens`. */
  function setBrandTokenModes(modes: BrandTokenModes, rootVariables: Readonly<Record<string, string>> = {}) {
    activeBrandTokenModes.value = modes
    activeThemeRootVariables.value = { ...rootVariables }
    includeBrandChromeInDark.value = false
    activeBrandTokens.value = modes[currentTheme.value]
    themeRootStyle.value = buildThemeModesRootCss(modes, rootVariables)

    if (process.server) return
    clearBrandTokenOverrides(document.documentElement)
  }

  function resetBrandTokens() {
    activeBrandTokens.value = null
    includeBrandChromeInDark.value = false
    activeBrandTokenModes.value = null
    activeThemeRootVariables.value = {}
    themeRootStyle.value = ''

    if (process.server) return

    clearBrandTokenOverrides(document.documentElement)
  }

  return {
    activeBrandTokens,
    activeBrandTokenModes,
    currentTheme,
    getStoredTheme,
    storeTheme,
    setTheme,
    applySessionTheme,
    setBrandTokens,
    setBrandTokenModes,
    resetBrandTokens,
  }
}

function collectCurrentBrandTokens(root: HTMLElement): BrandTokens | null {
  const get = (name: string) => getComputedStyle(root).getPropertyValue(name).trim()
  const primary = get('--cp-brand-primary')
  // Read secondary from INLINE style only (not the computed cascade). theme.css
  // always defines a `--cp-brand-secondary` default, so getComputedStyle would
  // never be empty and would reconstruct a secondary the surface never set.
  const secondary = root.style.getPropertyValue('--cp-brand-secondary').trim()
  const tertiary = get('--cp-brand-tertiary')
  const background = get('--cp-brand-background')
  const surface = get('--cp-brand-surface')
  const error = get('--cp-brand-error')
  const fontColor = get('--cp-brand-font')
  const sidebar = get('--cp-brand-sidebar')
  const border = get('--cp-brand-border')
  // `secondary` is optional and excluded from the completeness gate.
  if (!primary || !tertiary || !background || !surface || !error || !fontColor || !sidebar || !border) return null
  return { primary, tertiary, background, surface, error, fontColor, sidebar, border, ...(secondary ? { secondary } : {}) }
}

function hasCompleteBrandTokens(tokens: Partial<BrandTokens>): tokens is BrandTokens {
  return Boolean(
    tokens.primary &&
      tokens.tertiary &&
      tokens.background &&
      tokens.surface &&
      tokens.error &&
      tokens.fontColor &&
      tokens.sidebar &&
      tokens.border
  )
}

function mergeBrandTokens(base: BrandTokens, tokens: Partial<BrandTokens>): BrandTokens {
  const merged = { ...base, ...tokens }

  // `secondary` is an optional, per-surface accent. A COMPLETE token set that
  // omits it (community/preset themes always send the 8 required keys with no
  // secondary) must not inherit a previous surface's secondary — otherwise a
  // clientportal brandColor2 would bleed into a community theme via this merge.
  // Spreading `undefined` does not delete the key, so drop it explicitly.
  if (hasCompleteBrandTokens(tokens) && tokens.secondary === undefined) {
    delete merged.secondary
  }

  return merged
}

function resolveBrandTokens(tokens: Partial<BrandTokens>, currentTokens: BrandTokens | null): BrandTokens | null {
  if (currentTokens) {
    return mergeBrandTokens(currentTokens, tokens)
  }

  if (process.server) {
    if (hasCompleteBrandTokens(tokens)) {
      return tokens
    }

    return mergeBrandTokens(DEFAULT_BRAND_TOKENS, tokens)
  }

  const runtimeTokens = collectCurrentBrandTokens(document.documentElement)

  if (runtimeTokens) {
    const safeRuntimeTokens = sanitizeBrandTokens(runtimeTokens)
    if (hasCompleteBrandTokens(safeRuntimeTokens)) {
      return mergeBrandTokens(safeRuntimeTokens, tokens)
    }
  }

  return hasCompleteBrandTokens(tokens) ? tokens : null
}

function appendBrandDeclarations(decls: string[], tokens: Partial<BrandTokens>) {
  for (const [key, cssVar] of Object.entries(BRAND_CSS_MAP)) {
    const value = tokens[key as keyof BrandTokens]
    if (value) decls.push(`${cssVar}:${value}`)
  }
}

function appendScaleDeclarations(decls: string[], tokens: Partial<BrandTokens>, mode: Theme) {
  for (const [key, cssPrefix] of Object.entries(TOKEN_CSS_MAP)) {
    const hex = tokens[key as keyof BrandTokens]
    if (!hex) continue

    const tokenName = TOKEN_SCALE_NAMES[key as keyof BrandTokens]
    const scale = key === 'fontColor' ? getFontScaleFromBrand(hex, mode) : getColorScale(hex, tokenName, mode)
    const highrisePrefix = HIGHRISE_BRIDGE_MAP[key as keyof BrandTokens]

    for (const [step, value] of Object.entries(scale)) {
      if (!value) continue
      decls.push(`${cssPrefix}-${step}:${value}`)
      if (highrisePrefix) decls.push(`${highrisePrefix}-${step}:${value}`)
    }
  }
}

function getOnColorOptions(mode: Theme): OnColorStrategyOptions {
  return mode === Theme.DARK ? { minContrast: DARK_MODE_ON_COLOR_MIN_CONTRAST } : {}
}

function appendOnColorDeclarations(decls: string[], tokens: Partial<BrandTokens>, mode: Theme) {
  const onColorOptions = getOnColorOptions(mode)
  for (const { key, cssVar } of ON_COLOR_BINDINGS) {
    const value = tokens[key]
    if (value) decls.push(`${cssVar}:${pickOnColor(value, onColorOptions)}`)
  }
}

function buildThemeDeclarations(tokens: Partial<BrandTokens>, mode: Theme): string {
  const decls: string[] = []
  appendBrandDeclarations(decls, tokens)
  appendScaleDeclarations(decls, tokens, mode)
  appendOnColorDeclarations(decls, tokens, mode)
  return decls.join(';')
}

function buildThemeRootCss(tokens: Partial<BrandTokens>, mode: Theme): string {
  // Use `:root:root` (specificity 0,0,2,0) so the brand bridge always wins over
  // HighRise's shipped `:root{--primary-*: …}` defaults regardless of stylesheet load order.
  return ':root:root{' + buildThemeDeclarations(tokens, mode) + '}'
}

function buildThemeModesRootCss(modes: BrandTokenModes, rootVariables: Readonly<Record<string, string>> = {}): string {
  const sharedDeclarations = Object.entries(rootVariables)
    .map(([name, value]) => `${name}:${value}`)
    .join(';')
  const lightDeclarations = [sharedDeclarations, buildThemeDeclarations(modes.light, Theme.LIGHT)].filter(Boolean).join(';')

  return (
    ":root:root,[data-theme='light'][data-theme='light']{" +
    lightDeclarations +
    "}[data-theme='dark'][data-theme='dark']{" +
    buildThemeDeclarations(modes.dark, Theme.DARK) +
    '}'
  )
}

function applyBrandTokens(tokens: BrandTokens, mode: Theme, themeRootStyle: { value: string }, includeChromeInDark: boolean) {
  const effectiveTokens = getEffectiveBrandTokens(tokens, mode, includeChromeInDark)

  themeRootStyle.value = buildThemeRootCss(effectiveTokens, mode)

  if (process.server) return

  const root = document.documentElement

  for (const [key, cssVar] of Object.entries(BRAND_CSS_MAP)) {
    const value = effectiveTokens[key as keyof BrandTokens]
    if (value) {
      root.style.setProperty(cssVar, value)
    } else {
      // Optional / omitted token: clear any stale inline value so a prior
      // surface's colour cannot linger (e.g. portal secondary bleeding into a community,
      // or light neutrals overriding [data-theme='dark'] CSS defaults).
      root.style.removeProperty(cssVar)
    }
  }

  // Set each token scale (e.g. surface → --cp-surface-50 … --cp-surface-900) from brand hex
  for (const [key, cssPrefix] of Object.entries(TOKEN_CSS_MAP)) {
    const hex = effectiveTokens[key as keyof BrandTokens]
    if (hex) {
      const tokenName = TOKEN_SCALE_NAMES[key as keyof BrandTokens]
      const scale = key === 'fontColor' ? getFontScaleFromBrand(hex, mode) : getColorScale(hex, tokenName, mode)
      applyColorScale(root, cssPrefix, scale)

      const highrisePrefix = HIGHRISE_BRIDGE_MAP[key as keyof BrandTokens]
      if (highrisePrefix) {
        applyColorScale(root, highrisePrefix, scale)
      }
    } else {
      // Optional / omitted token: drop its stale inline scale so the theme.css default
      // (via the cascade) takes over instead of the previous surface's colour.
      removeColorScale(root, cssPrefix)
    }
  }

  computeOnColors(root, effectiveTokens, mode)
}

/**
 * Partial branding (for example Client Portal accent colors) must not re-apply
 * light chrome in dark mode. Complete mode-aware themes can opt in to applying
 * their resolved dark background, surface, font, and border tokens.
 */
function getEffectiveBrandTokens(tokens: BrandTokens, mode: Theme, includeChromeInDark: boolean): Partial<BrandTokens> {
  if (mode !== Theme.DARK || includeChromeInDark) return tokens

  return {
    primary: tokens.primary,
    ...(tokens.secondary ? { secondary: tokens.secondary } : {}),
    tertiary: tokens.tertiary,
    error: tokens.error,
    sidebar: tokens.sidebar,
  }
}

function clearBrandTokenOverrides(root: HTMLElement) {
  for (const cssVar of Object.values(BRAND_CSS_MAP)) {
    root.style.removeProperty(cssVar)
  }

  for (const cssPrefix of Object.values(TOKEN_CSS_MAP)) {
    removeColorScale(root, cssPrefix)
  }

  for (const cssPrefix of Object.values(HIGHRISE_BRIDGE_MAP)) {
    if (cssPrefix) {
      removeColorScale(root, cssPrefix)
    }
  }

  for (const { cssVar } of ON_COLOR_BINDINGS) {
    root.style.removeProperty(cssVar)
  }
}

function computeOnColors(root: HTMLElement, tokens: Partial<BrandTokens>, mode: Theme) {
  const computed = getComputedStyle(root)
  const onColorOptions = getOnColorOptions(mode)

  for (const { key, cssVar, scaleVar } of ON_COLOR_BINDINGS) {
    const token = tokens[key]
    if (token) {
      // Prefer the brand token (button fill) over the scale step. Fall back to
      // scaleVar only when the token string is empty after trim.
      const background = token.trim() || computed.getPropertyValue(scaleVar).trim()
      root.style.setProperty(cssVar, pickOnColor(background, onColorOptions))
    } else {
      // Clear stale inline contrast colour when the token is omitted (e.g. secondary bleed).
      root.style.removeProperty(cssVar)
    }
  }
}

function getFontScaleFromBrand(brandFontHex: string, mode: Theme) {
  if (mode === Theme.DARK) {
    return getColorScale(brandFontHex, 'font', mode)
  }

  const normalizedBrand = normalizeHex(brandFontHex)
  if (normalizedBrand === DEFAULT_FONT_SCALE[800] || normalizedBrand === DEFAULT_FONT_SCALE[900]) {
    return { ...DEFAULT_FONT_SCALE }
  }

  const brandRgb = hexToRgb(normalizedBrand)
  const default800Rgb = hexToRgb(DEFAULT_FONT_SCALE[800])
  if (!brandRgb || !default800Rgb) {
    return getColorScale(brandFontHex, 'font', mode)
  }

  const scale: Record<number, string> = {}
  for (const [stepKey, stepHex] of Object.entries(DEFAULT_FONT_SCALE)) {
    const step = Number(stepKey)
    if (step === 800) {
      scale[step] = normalizedBrand
      continue
    }

    const stepRgb = hexToRgb(stepHex)
    if (!stepRgb) continue

    scale[step] = rgbToHex({
      r: clamp(brandRgb.r + (stepRgb.r - default800Rgb.r)),
      g: clamp(brandRgb.g + (stepRgb.g - default800Rgb.g)),
      b: clamp(brandRgb.b + (stepRgb.b - default800Rgb.b)),
    })
  }

  return scale
}

function normalizeHex(hex: string): string {
  const trimmed = hex.trim().toLowerCase()
  if (!trimmed.startsWith('#')) return trimmed
  if (trimmed.length === 4) {
    return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`
  }
  return trimmed
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = normalizeHex(hex)
  if (!/^#[0-9a-f]{6}$/.test(normalized)) return null

  return {
    r: Number.parseInt(normalized.slice(1, 3), 16),
    g: Number.parseInt(normalized.slice(3, 5), 16),
    b: Number.parseInt(normalized.slice(5, 7), 16),
  }
}

function rgbToHex(rgb: { r: number; g: number; b: number }): string {
  const toHex = (value: number) => value.toString(16).padStart(2, '0')
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`
}

function clamp(value: number): number {
  return Math.max(0, Math.min(255, value))
}
